import { useCallback, useEffect, useMemo, useRef } from 'react';

type Query<T = any, R = any> = {
  _id: number;
  eventName: string;
  payload?: T;
  resolve?: (payload: R) => void;
  reject?: () => void;
  status: 'send' | 'resolved' | 'rejected';
};

type Listener<T = any, R = any> = {
  eventName: string;
  callback: (payload: T) => R | Promise<R>;
};

type MessageBundle<T = any> = {
  __RNQueryMessage: string;
  eventName: string;
  isResponse: boolean;
  payload?: T;
  __index: number;
};

export type UseRNQuery = {
  query: <T = any, R = any>(
    eventName: string,
    payload?: T
  ) => Promise<R | void>;
  send: <T = any>(
    eventName: string,
    params?: { payload?: T; __index?: number; isResponse?: boolean }
  ) => void;
  register: (
    post: (message: string) => void,
    receive?: (cb: (message: string) => void) => void
  ) => void;
  on: <T = any, R = any>(
    eventName: string,
    callback: (payload: T) => R | Promise<R>
  ) => OffCall;
  onMessage: (message: string) => void;
};

type PostFunc = (message: string) => void;

type OffCall = () => void;

export type SQLSelectResult<T> = {
  error?: string;
  rows?: {
    length: number;
    _array: T[];
  };
  rowsAffected?: number;
};

export const useRNQuery = (): UseRNQuery => {
  const queries = useRef<Map<number, Query>>(new Map());
  const listeners = useRef<Map<number, Listener>>(new Map());
  const listenersCounterRef = useRef<number>(1);
  const queriesCounterRef = useRef<number>(1);
  const postRef = useRef<PostFunc>();

  const send = useMemo(
    () =>
      <T = any>(
        eventName: string,
        params?: { payload?: T; __index?: number; isResponse?: boolean }
      ): void => {
        if (!postRef.current) {
          console.log('Message interface isn`t ready');
        }
        postRef.current?.(
          JSON.stringify({
            __RNQueryMessage: `${eventName}_${
              params?.__index || ''
            }_${Date.now()}`,
            eventName,
            isResponse: false,
            ...params,
          })
        );
      },
    []
  );

  const onMessage = useCallback((message: string) => {
    if (!message) {
      return;
    }
    try {
      const obj = JSON.parse(message);
      if (obj && typeof obj === 'object' && obj.__RNQueryMessage) {
        const { eventName, __index, __RNQueryMessage, payload, isResponse } =
          obj as MessageBundle;
        if (isResponse) {
          const query = queries.current.get(__index);
          if (query) {
            query.resolve?.(payload);
          }
        } else {
          listeners.current.forEach((listener) => {
            if (listener.eventName !== eventName) {
              return;
            }

            const response = listener.callback(payload);
            if (!__index) {
              return;
            }
            if (response?.then) {
              response.then((payload: any) => {
                send(eventName, {
                  payload,
                  __index,
                  isResponse: true,
                });
              });
              return;
            }
            send(eventName, {
              payload: response,
              __index,
              isResponse: true,
            });
          });
        }
      }
    } catch (e) {
      console.log('Parse error', e.stack, message);
      throw e;
      // do nothing
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = useCallback(
    (
      post: (message: string) => void,
      receive?: (cb: (message: string) => void) => void
    ) => {
      postRef.current = (message: string) => post(message);
      receive?.(onMessage);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const on = useMemo(
    () =>
      <T = any, R = any>(
        eventName: string,
        callback: (payload: T) => R | Promise<R>
      ): OffCall => {
        const key = listenersCounterRef.current++;

        listeners.current.set(key, {
          eventName,
          callback,
        });

        return () => {
          return listeners.current.delete(key);
        };
      },
    []
  );

  const query = useMemo(
    () =>
      <T = any, R = any>(eventName: string, payload?: T): Promise<R | void> => {
        const query: Query<T> = {
          _id: queriesCounterRef.current++,
          eventName,
          payload,
          status: 'send',
        };

        send<T>(eventName, { payload, __index: query._id });

        queries.current.set(query._id, query);

        return new Promise<R | void>((_resolve, _reject) => {
          query.resolve = (data: R | null) => {
            query.status = 'resolved';
            _resolve(data || undefined);
          };
          query.reject = () => {
            query.status = 'rejected';
            _reject();
          };
        });
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const handle = setInterval(() => {
      queries.current.forEach(({ status }, key) => {
        if (status !== 'send') {
          queries.current.delete(key);
        }
      });
    }, 5000);
    return () => {
      clearInterval(handle);
    };
  }, []);

  return {
    register,
    on,
    send,
    query,
    onMessage,
  };
};
